// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * Why the last session ended (W74).
 *
 * The API client learns this on its way out — the refresh endpoint answers 401 with
 * "signed out due to inactivity (idle_timeout)" — and the login page has to explain
 * it after a full-page redirect. sessionStorage carries it across that redirect:
 * unlike a query parameter it stays out of browser history and any referrer header,
 * and unlike localStorage it dies with the tab.
 *
 * Its own module on purpose: both the API client and the login page need it, and
 * each of them is mocked in the other's tests.
 */

export const LOGOUT_REASON_KEY = 'clubview_logout_reason';

/** Reasons worth showing a person. Anything else is treated as no reason at all. */
const KNOWN_REASONS = ['idle_timeout'] as const;
export type LogoutReason = (typeof KNOWN_REASONS)[number];

function isKnown(value: string | null): value is LogoutReason {
  return value !== null && (KNOWN_REASONS as readonly string[]).includes(value);
}

/**
 * Record why the session ended. Silently does nothing where storage is unavailable —
 * the try/catch covers a missing `sessionStorage` as well as one that refuses, so no
 * optional chaining is needed (and an unreachable `?.` branch fails the coverage gate).
 */
export function setLogoutReason(reason: string): void {
  try {
    window.sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    // private mode, blocked storage — the login page falls back to its generic copy
  }
}

/**
 * Read the reason and clear it, so returning to /login later does not repeat a
 * message about a session that ended long ago. Unknown values are discarded too.
 */
export function consumeLogoutReason(): LogoutReason | null {
  try {
    const value = window.sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (value !== null) window.sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return isKnown(value) ? value : null;
  } catch {
    return null;
  }
}
